import React, { useState } from 'react'
import styled from 'styled-components'
import { searchUser } from 'apis/account'

const Wrapper = styled.div`
  padding: 2rem;
  min-height: 100vh;
  background-color: #f5f5f5;
`

const Title = styled.h2`
  font-weight: 600;
  margin-bottom: 0;
`

const Divider = styled.hr`
  max-width: 1000px;
  height: 1px;
  background-color: #e0e0e0;
  border: none;
  margin: 1rem 0;
`

const SearchRow = styled.div`
  display: flex;
  gap: 0.75rem;
  max-width: 600px;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`

const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
`

const SearchButton = styled.button`
  background-color: #1565c0;
  color: white;
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  &:hover { background-color: #0d47a1; }
  &:disabled { background-color: #ccc; cursor: not-allowed; }
`

const ErrorMsg = styled.div`
  color: #c62828;
  margin-bottom: 1rem;
  font-size: 14px;
`

const Card = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.1);
  padding: 1.5rem;
  max-width: 1000px;
  margin-bottom: 1.5rem;
`

const CardTitle = styled.h4`
  font-weight: 600;
  margin-bottom: 1rem;
  color: #1565c0;
  border-bottom: 2px solid #e3f2fd;
  padding-bottom: 0.5rem;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem 1.5rem;
  @media (max-width: 768px) { grid-template-columns: 1fr 1fr; }
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
`

const Label = styled.span`
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`

const Value = styled.span`
  font-size: 14px;
  color: #212121;
  font-weight: 500;
  word-break: break-all;
`

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${({ type }) =>
    type === 'success' ? '#e8f5e9' :
    type === 'danger'  ? '#ffebee' :
    type === 'warning' ? '#fff8e1' : '#f5f5f5'};
  color: ${({ type }) =>
    type === 'success' ? '#2e7d32' :
    type === 'danger'  ? '#c62828' :
    type === 'warning' ? '#f57f17' : '#616161'};
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
`

const Th = styled.th`
  text-align: left;
  padding: 8px 12px;
  background-color: #e3f2fd;
  color: #1565c0;
  font-weight: 600;
  border-bottom: 2px solid #bbdefb;
`

const Td = styled.td`
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
  color: #424242;
`

const EmptyState = styled.div`
  color: #888;
  font-size: 14px;
  padding: 1rem 0;
`

const formatDate = val =>
  val ? new Date(val).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'

const boolBadge = (val, trueLabel = 'Yes', falseLabel = 'No') =>
  val
    ? <Badge type="success">{trueLabel}</Badge>
    : <Badge type="danger">{falseLabel}</Badge>

const UserSearch = () => {
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)

  const handleSearch = async () => {
    const trimmed = query.trim()
    if (!trimmed) return

    setLoading(true)
    setError('')
    setResult(null)

    const isEmail = trimmed.includes('@')
    try {
      const data = await searchUser(
        isEmail ? { email: trimmed } : { user_id: trimmed }
      )
      setResult(data.result)
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.error) ||
        (err && err.response && err.response.data && err.response.data.detail) ||
        'User not found or an error occurred.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch()
  }

  const { user, profile, subscriptions } = result || {}

  const subStatusBadgeType = status => {
    if (!status) return 'default'
    const s = status.toLowerCase()
    if (s === 'active') return 'success'
    if (s === 'canceled' || s === 'cancelled') return 'danger'
    return 'warning'
  }

  return (
    <Wrapper>
      <Title>User Search</Title>
      <Divider />

      <SearchRow>
        <SearchInput
          type="text"
          placeholder="Search by email or user ID"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <SearchButton onClick={handleSearch} disabled={loading || !query.trim()}>
          {loading ? 'Searching...' : 'Search'}
        </SearchButton>
      </SearchRow>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {user && (
        <>
          {/* User Details */}
          <Card>
            <CardTitle>User Details</CardTitle>
            <Grid>
              <Field><Label>User ID</Label><Value>{user.id}</Value></Field>
              <Field><Label>Apex Account No(s)</Label><Value>{user.apex_account_numbers && user.apex_account_numbers.length > 0 ? user.apex_account_numbers.join(', ') : '—'}</Value></Field>
              <Field><Label>First Name</Label><Value>{user.first_name || '—'}</Value></Field>
              <Field><Label>Last Name</Label><Value>{user.last_name || '—'}</Value></Field>
              <Field><Label>Email</Label><Value>{user.email}</Value></Field>
              <Field><Label>Okta UID</Label><Value>{user.okta_uid || '—'}</Value></Field>
              <Field><Label>Referral Code</Label><Value>{user.referral_code || '—'}</Value></Field>
              <Field><Label>Date Joined</Label><Value>{formatDate(user.date_joined)}</Value></Field>
              <Field><Label>Last Login</Label><Value>{formatDate(user.last_login)}</Value></Field>
              <Field><Label>Register Step</Label><Value>{user.register_step_status || '—'}</Value></Field>
              <Field><Label>Active</Label><Value>{boolBadge(user.is_active)}</Value></Field>
              <Field><Label>Email Verified</Label><Value>{boolBadge(user.is_email_verified)}</Value></Field>
              <Field><Label>Staff</Label><Value>{boolBadge(user.is_staff)}</Value></Field>
              <Field><Label>Admin</Label><Value>{boolBadge(user.is_admin)}</Value></Field>
              <Field><Label>Test User</Label><Value>{boolBadge(user.is_test, 'Yes', 'No')}</Value></Field>
              <Field><Label>Trader</Label><Value>{boolBadge(user.is_trader)}</Value></Field>
            </Grid>
          </Card>

          {/* Profile Details */}
          {profile && (
            <Card>
              <CardTitle>Profile</CardTitle>
              <Grid>
                <Field><Label>Phone</Label><Value>{profile.phone || '—'}</Value></Field>
                <Field><Label>Date of Birth</Label><Value>{profile.date_of_birth || '—'}</Value></Field>
                <Field><Label>Country</Label><Value>{profile.country || '—'}</Value></Field>
                <Field><Label>State</Label><Value>{profile.state || '—'}</Value></Field>
                <Field><Label>City</Label><Value>{profile.city || '—'}</Value></Field>
                <Field><Label>Zipcode</Label><Value>{profile.zipcode || '—'}</Value></Field>
                <Field><Label>Investing Exp.</Label><Value>{profile.investing_exp || '—'}</Value></Field>
                <Field><Label>Household Income</Label><Value>{profile.household_income || '—'}</Value></Field>
                <Field><Label>Est. Net Worth</Label><Value>{profile.estimated_net_worth || '—'}</Value></Field>
                <Field><Label>UTM Source</Label><Value>{profile.utm_source || '—'}</Value></Field>
                <Field><Label>UTM Medium</Label><Value>{profile.utm_medium || '—'}</Value></Field>
                <Field><Label>UTM Campaign</Label><Value>{profile.utm_campaign || '—'}</Value></Field>
              </Grid>
            </Card>
          )}

          {/* Subscriptions */}
          <Card>
            <CardTitle>Subscriptions</CardTitle>
            {subscriptions && subscriptions.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <Th>ID</Th>
                    <Th>Plan</Th>
                    <Th>Price</Th>
                    <Th>Status</Th>
                    <Th>Start Date</Th>
                    <Th>End Date</Th>
                    <Th>Features Allowed</Th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map(sub => (
                    <tr key={sub.id}>
                      <Td>{sub.id}</Td>
                      <Td>{sub.plan_name || '—'}</Td>
                      <Td>{sub.price != null ? `$${sub.price}` : '—'}</Td>
                      <Td>
                        <Badge type={subStatusBadgeType(sub.status)}>
                          {sub.status || '—'}
                        </Badge>
                      </Td>
                      <Td>{formatDate(sub.plan_start_date)}</Td>
                      <Td>{sub.plan_end_date ? formatDate(sub.plan_end_date) : '—'}</Td>
                      <Td>{sub.feature_allowed || '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <EmptyState>No subscriptions found for this user.</EmptyState>
            )}
          </Card>
        </>
      )}
    </Wrapper>
  )
}

export default UserSearch
